import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Target, CircleDot } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function CalendarView({ projects }) {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  // Build events per day from Project Target Dates + open Action Due Dates (no phase dates).
  const byDay = {};
  const push = (k, ev) => { if (k) (byDay[k] = byDay[k] || []).push(ev); };
  projects.forEach((p) => {
    if (p.target_date) push(String(p.target_date).slice(0, 10), { type: "target", project: p });
    (p.action_due || []).forEach((a) => { if (a.due_date) push(String(a.due_date).slice(0, 10), { type: "action", project: p, action: a }); });
  });
  // Targets first, then actions, within a day.
  Object.values(byDay).forEach((list) => list.sort((a, b) => (a.type === b.type ? 0 : a.type === "target" ? -1 : 1)));

  const first = new Date(cursor.y, cursor.m, 1);
  const startDow = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const shift = (delta) => setCursor((c) => { const nm = c.m + delta; return { y: c.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 }; });
  const pad = (n) => String(n).padStart(2, "0");
  const today = new Date();
  const isToday = (d) => d && today.getFullYear() === cursor.y && today.getMonth() === cursor.m && today.getDate() === d;
  const openEvent = (ev) => ev.type === "target"
    ? navigate(`/projects/${ev.project.project_id}`)
    : navigate(`/projects/${ev.project.project_id}?phase=${ev.action.phase_id}`);

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="projects-calendar-view">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-4">
          <h3 className="font-bold text-slate-900" data-testid="calendar-month-label">{MONTHS[cursor.m]} {cursor.y}</h3>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1"><Target className="h-3 w-3 text-slate-900" />Project target</span>
            <span className="inline-flex items-center gap-1"><CircleDot className="h-3 w-3 text-amber-600" />Action due</span>
          </div>
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {DOW.map((d) => <div key={d} className="text-[10px] uppercase tracking-wide text-slate-400 font-medium text-center py-1">{d}</div>)}
        {cells.map((d, i) => {
          const key = d ? `${cursor.y}-${pad(cursor.m + 1)}-${pad(d)}` : null;
          const evs = key ? (byDay[key] || []) : [];
          return (
            <div key={i} className={`min-h-[84px] rounded-lg border p-1 ${d ? "border-slate-200" : "border-transparent"} ${isToday(d) ? "ring-1 ring-slate-900" : ""}`}>
              {d && <div className="text-xs text-slate-500 px-1">{d}</div>}
              <div className="space-y-1 mt-0.5">
                {evs.slice(0, 4).map((ev, j) => ev.type === "target" ? (
                  <button key={`t-${ev.project.project_id}`} onClick={() => openEvent(ev)} data-testid={`calendar-event-${ev.project.project_number}`}
                    title={`Target · ${ev.project.project_number} — ${ev.project.customer_name}`}
                    className="w-full text-left text-[10px] leading-tight rounded bg-slate-900 text-white px-1.5 py-0.5 truncate hover:bg-slate-700">
                    {ev.project.project_number} · {ev.project.customer_name}
                  </button>
                ) : (
                  <button key={`a-${ev.action.action_id}`} onClick={() => openEvent(ev)} data-testid={`calendar-action-${ev.action.action_id}`}
                    title={`${ev.action.title} · ${ev.project.project_number} — ${ev.project.customer_name}`}
                    className="w-full text-left text-[10px] leading-tight rounded border border-amber-300 bg-amber-50 text-amber-900 px-1.5 py-0.5 truncate hover:bg-amber-100">
                    {ev.action.title} · {ev.project.project_number}
                  </button>
                ))}
                {evs.length > 4 && <div className="text-[10px] text-slate-400 px-1">+{evs.length - 4} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
